/* ============================================================
   CHANNEL LOADER — runtime source resolution + merge.
   ------------------------------------------------------------
   The lineup the app shows = REMOTE channels (Netlify data service,
   cron-refreshed blob) ∪ USER channels (localStorage, added in-app).
   Both are validated through the SAME canonical schema, so they're
   the same shape and merge by dial number. If the data service is
   unreachable, the built-in static lineup stands in for remote.

   This uniformity is the whole point: "add a channel" is just
   "validate a playlist into a Channel and save it to localStorage".
   ============================================================ */
import { parsePayload, parseChannels, type Channel } from '@/schema/channel'
import { CHANNELS } from './channels'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const ENDPOINT = `${API_BASE}/api/channels`
const USER_KEY = 'orion.tv.channels.v1'

/** Read + validate the user's custom channels from localStorage. */
export function loadUserChannels(): Channel[] {
  try {
    const raw = JSON.parse(localStorage.getItem(USER_KEY) || '[]')
    return parseChannels(raw, 'user')
  } catch {
    return []
  }
}

/** Persist the user's custom channels (already canonical Channels). */
export function saveUserChannels(channels: Channel[]): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(channels))
  } catch {
    /* storage unavailable; ignore */
  }
}

/** Fetch + validate the remote lineup, or the static fallback. Never rejects. */
async function loadRemote(
  signal?: AbortSignal,
): Promise<{ channels: Channel[]; source: 'remote' | 'static' }> {
  try {
    const res = await fetch(ENDPOINT, { signal })
    if (!res.ok) throw new Error(`channels endpoint ${res.status}`)
    const payload = parsePayload(await res.json())
    const channels = parseChannels(payload?.channels ?? [], 'remote')
    if (!channels.length) throw new Error('empty lineup')
    return { channels, source: 'remote' }
  } catch {
    return { channels: CHANNELS, source: 'static' }
  }
}

/**
 * Merge two channel lists by dial number. User channels win on conflict (you
 * can override a remote channel locally); otherwise they extend the lineup.
 */
function mergeByNumber(base: Channel[], overrides: Channel[]): Channel[] {
  const byNum = new Map<number, Channel>()
  for (const c of base) byNum.set(c.number, c)
  for (const c of overrides) byNum.set(c.number, c)
  return [...byNum.values()].sort((a, b) => a.number - b.number)
}

/** Load the full lineup: remote/static ∪ localStorage, all canonical. */
export async function loadChannels(signal?: AbortSignal): Promise<{
  channels: Channel[]
  source: 'remote' | 'static'
}> {
  const { channels: remote, source } = await loadRemote(signal)
  const user = loadUserChannels()
  const enrichedRemote = remote.map((ch) => {
    const builtin = CHANNELS.find((c) => c.number === ch.number)
    if (builtin && builtin.breaks && !ch.breaks) {
      return { ...ch, breaks: builtin.breaks }
    }
    return ch
  })
  return { channels: mergeByNumber(enrichedRemote, user), source }
}
